import { Button, IconButton, Popover, TextField } from '@mui/material';
import { SxProps } from '@mui/system';
import type { ChangeEvent, KeyboardEvent, MouseEvent } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useStyling } from '../../../common/Theme';
import Loading from '../../../common/components/Loading';
import { useIsWithinBreakpoints } from '../../../common/hooks/useIsWithinBreakpoints';
import { useI18n } from '../../../common/i18n/I18n';
import { Icons } from '../../../common/icons/Icons';
import { StyledBadge } from './StyledBadge';

type SearchPanelProps = {
    id: string;
    loading: boolean;
    query: string;
    setQuery?: (query: string) => void;
    sx?: SxProps;
    searching?: boolean;
    searchPlaceholder?: string;
    mobile?: boolean;
    /** When true, search runs on each keystroke (client-side filtering). */
    isLocal?: boolean;
};

type SearchPanelInputProps = Required<Pick<SearchPanelProps, 'id' | 'loading' | 'query' | 'searching'>> &
    Pick<SearchPanelProps, 'searchPlaceholder' | 'isLocal'> & {
        setQuery: (query: string) => void;
    };

type SearchInputProps = SearchPanelInputProps & Pick<SearchPanelProps, 'sx'>;

type MobileButtonProps = SearchPanelInputProps;

function SearchInput({
    id,
    loading,
    query,
    setQuery,
    sx = undefined,
    searching,
    searchPlaceholder,
    isLocal = false
}: SearchInputProps) {
    const [term, setTerm] = useState<string>(query);
    const { theme } = useStyling();
    const ref = useRef<HTMLInputElement | null>(null);
    const buttonSx: SxProps = {
        ml: 1,
        '&:hover': { cursor: 'pointer' },
        fontColor: loading ? theme.palette.text.disabled : theme.palette.text.primary
    };
    const placeholder = useI18n('filter.search.label');

    useEffect(() => {
        setTerm(query);
    }, [query]);

    const handleTermChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const nextTerm = e.target.value;
            setTerm(nextTerm);
            // Client-side search updates results while typing (same pattern as NotesSearchInput).
            if (isLocal) {
                setQuery(nextTerm);
            }
        },
        [isLocal, setQuery]
    );

    const handleSearch = useCallback(() => {
        setQuery(term);
    }, [setQuery, term]);

    const handleClear = useCallback(() => {
        setTerm('');
        setQuery('');
    }, [setQuery]);

    const handleEnterSearch = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                setQuery(term);
            }
        },
        [setQuery, term]
    );

    return (
        <TextField
            sx={{
                ...sx,
                // Not ideal but it seems to be the only way to remove the blue background added by chrome.
                ' input:-webkit-autofill, input:-webkit-autofill:focus ': {
                    transition: 'background-color 0s 600000s, color 0s 600000s !important'
                }
            }}
            id={`${id}-input`}
            inputRef={ref}
            // sx={{ width: '200px' }}

            value={term}
            onChange={handleTermChange}
            onKeyDown={handleEnterSearch}
            variant={'outlined'}
            size={'small'}
            InputProps={{
                sx: { height: '32px' },
                endAdornment: (
                    <>
                        {searching ? (
                            <Loading size="16px" sx={{ width: 'unset' }} />
                        ) : (
                            <Icons.Search sx={buttonSx} data-testid="SearchOutlinedIcon" onClick={handleSearch} />
                        )}
                        <Icons.Close sx={buttonSx} onClick={handleClear} />
                    </>
                ),
                autoFocus: false,
                inputProps: {
                    placeholder: searchPlaceholder || placeholder
                }
            }}
        />
    );
}

function MobileButton({
    id,
    loading,
    query,
    setQuery,
    searching,
    searchPlaceholder,
    isLocal = false
}: MobileButtonProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);
    const showLabel = useIsWithinBreakpoints(['s']);
    const label = useI18n('facets.search.label');
    const { theme } = useStyling();

    const onClose = useCallback(() => {
        setAnchorEl(null);
    }, []);
    const onClick = (e: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(e.currentTarget);
    };
    const handleMobileSetQuery = useCallback(
        (nextQuery: string) => {
            setQuery(nextQuery);
            // Server search closes after submit; local search stays open while typing.
            if (!isLocal) {
                onClose();
            }
        },
        [isLocal, onClose, setQuery]
    );
    return (
        <>
            <StyledBadge>
                {showLabel ? (
                    <Button sx={{ whiteSpace: 'nowrap' }} onClick={onClick} variant="text" startIcon={<Icons.Search />}>
                        {label}
                    </Button>
                ) : (
                    <IconButton
                        sx={{
                            color: theme.palette.primary.main,
                            px: 0.5,
                            py: 0
                        }}
                        onClick={onClick}
                    >
                        <Icons.Search />
                    </IconButton>
                )}
            </StyledBadge>
            <Popover
                id={`${id}-mobile-popover`}
                open={open}
                anchorEl={anchorEl}
                onClose={onClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                slotProps={{
                    paper: {
                        sx: {
                            overflow: 'hidden'
                        }
                    }
                }}
            >
                <SearchInput
                    searchPlaceholder={searchPlaceholder}
                    id={id}
                    loading={loading}
                    searching={searching}
                    query={query}
                    isLocal={isLocal}
                    setQuery={handleMobileSetQuery}
                />
            </Popover>
        </>
    );
}

export default function SearchPanel({
    id,
    loading,
    query,
    setQuery,
    sx = {},
    searching = false,
    searchPlaceholder,
    mobile = true,
    isLocal = false
}: SearchPanelProps) {
    const isMobile = useIsWithinBreakpoints(['s', 'xs']);

    return typeof setQuery === 'function' ? (
        isMobile && mobile ? (
            <MobileButton
                id={id}
                loading={loading}
                searching={searching}
                query={query}
                setQuery={setQuery}
                searchPlaceholder={searchPlaceholder}
                isLocal={isLocal}
            />
        ) : (
            <SearchInput
                id={id}
                loading={loading}
                searching={searching}
                query={query}
                setQuery={setQuery}
                sx={sx}
                searchPlaceholder={searchPlaceholder}
                isLocal={isLocal}
            />
        )
    ) : (
        <></>
    );
}
